import { EventEmitter } from 'events';

import Events from '../Events';
import FileTypes from '../FileTypes';

import GHXLoader from '../GHXLoader';
import MaterialTypes from '../MaterialTypes';
import GHParser from '../gh/GHParser';

import GHHandle from '../gh/geometry/GHHandle';
import GHLinePath from '../gh/geometry/GHLinePath';

import EExportOutline from '../gh/components/EExportOutline';

import BaseController from './BaseController';

const unit = 0.001;
// const unit = 1

export default class GHXController extends BaseController {
  get fileType () {
    return FileTypes.GHX;
  }

  setupScene (canvas) {
    super.setupScene(canvas);

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const scene = new THREE.Scene();

    const handles = new THREE.Object3D();
    const pathContainer = new THREE.Object3D();
    pathContainer.scale.set(unit, unit, unit);
    pathContainer.rotation.set(-Math.PI * 0.5, 0, 0);

    var INTERSECTED = null;

    scene.add(pathContainer);
    scene.add(handles);

    this.raycaster = raycaster;
    this.mouse = mouse;
    this.INTERSECTED = INTERSECTED;

    this.parser = undefined;
    this.pathContainer = pathContainer;
    this.handles = handles;
    this.selectedHandle = null;
    this.tapPos = new THREE.Vector2();

    document.addEventListener('mousedown', this.onDocumentMouseDown.bind(this), false);
    document.addEventListener('mousemove', this.onDocumentMouseMove.bind(this), false);
    document.addEventListener('mouseup', this.onDocumentMouseUp.bind(this), false);
    document.addEventListener('touchstart', this.onDocumentMouseDown.bind(this), false);
    document.addEventListener('touchmove', this.onDocumentMouseMove.bind(this), false);
    document.addEventListener('touchend', this.onDocumentMouseUp.bind(this), false);
  }

  destroy () {
    document.removeEventListener('mousedown', this.onDocumentMouseDown.bind(this));
    document.removeEventListener('mousemove', this.onDocumentMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onDocumentMouseUp.bind(this));
    document.removeEventListener('touchstart', this.onDocumentMouseDown.bind(this));
    document.removeEventListener('touchmove', this.onDocumentMouseMove.bind(this));
    document.removeEventListener('touchend', this.onDocumentMouseUp.bind(this));
    super.destroy();
  }

  reset () {
    super.reset();
    this.resetHandles();
  }

  resetHandles () {
    let children = this.handles.children;
    for (var i = children.length - 1; i >= 0; i--) {
      this.handles.remove(children[i]);
    }
  }

  get sliders () {
    if (this.parser !== undefined) {
      return this.parser.sliders;
    }
    return [];
  }

  get toggles () {
    if (this.parser !== undefined) {
      return this.parser.toggles;
    }
    return [];
  }

  get valueLists () {
    if (this.parser !== undefined) {
      return this.parser.valueLists;
    }
    return [];
  }

  get materialLists () {
    if (this.parser !== undefined) {
      return this.parser.materialLists;
    }
    return [];
  }

  get exportComponents () {
    if (this.parser !== undefined) {
      let components = (this.parser.components.filter(component => (component instanceof EExportOutline)));
      return components.map(component => {
        component.paths = component.paths.map(path => {
          return path;
        });
        return component;
      });
    }
    return [];
  }

  get materialThickness () {
    let found = this.parser.components.find((component) => {
      let geom = component.ghGeometryObject;
      if (geom !== undefined) {
        return geom.nickName.toLowerCase() === 'material thickness';
      }
      return false;
    });

    // Panel
    if (found !== undefined) {
      let outputs = Object.values(found.outputs);
      if (outputs.length > 0) {
        let output = outputs[0];
        let value = output.paramObject.value;
        let branches = value.branches;
        if (branches.length > 0 && branches[0].length > 0) {
          return Number(branches[0][0]);
        }
      }
    }

    return super.materialThickness;
  }

  update (ui) {
    if (this.parser !== undefined) {
      this.updateMesh();
    }
    super.update();
  }

  centerize (meshes) {
    let summary = this.getUnion(meshes);
    let center = new THREE.Vector3();
    summary.getCenter(center);

    let z = summary.min.z * unit;
    this.container.position.set(-center.x * unit, -z, -center.y * unit);
    this.handles.position.set(-center.x * unit, -z, -center.y * unit);
  }

  parse (ghx, report = true) {
    this.reset();

    if (report) {
      try {
        this.parser = new GHParser(ghx, {
          material: this.materialManager,
          paint: this.paintManager
        });
        this.parser.sequence();
      } catch (e) {
        alert(e.message);
      }
    } else {
      this.parser = new GHParser(ghx, {
        material: this.materialManager,
        paint: this.paintManager
      });
      this.parser.sequence();
    }

    this.parser.meshes.forEach((mesh, i) => {
      mesh.scale.set(unit, unit, unit);
      mesh.rotation.set(-Math.PI * 0.5, 0, 0);

      if (mesh.geometry.visible) {
        mesh.setMaterial(this.materialType, i);
        mesh.castShadow = true;
      } else {
        mesh.setMaterial(MaterialTypes.TRANSPARENT, i);
        mesh.castShadow = false;
      }
      mesh.setTextures(Object.values(this.textures)[0]);

      this.container.add(mesh);
    });

    this.parser.handleMeshes.forEach((handleMesh, i) => {
      handleMesh.scale.set(unit, unit, unit);
      handleMesh.rotation.set(-Math.PI * 0.5, 0, 0);
      handleMesh.castShadow = false;
      this.handles.add(handleMesh);
    });

    this.centerize(this.parser.meshes);
    this.boundingBox.update(this.container);
    this.updateGrid();
  }

  load (path, report, onLoad, onProgress, onError) {
    const loader = new GHXLoader();
    loader.load(
      path,
      (ret) => {
        this.parse(ret, report);
        if (onLoad !== undefined) {
          onLoad(this);
        }
      },
      onProgress, onError
    );
  }

  projectTo2D (pos3d) {
    let pos = new THREE.Vector3(pos3d.x * unit, pos3d.y * unit, -pos3d.z * unit);
    let projected = pos.project(this.camera);

    let pos2d = new THREE.Vector2(projected.x, projected.y);

    return pos2d;
  }

  calcIntersecttLines (p1, p2, p3, p4) {
    let p13 = p1.clone();
    p13.sub(p3);

    let p21 = p2.clone();
    p21.sub(p1);

    let p43 = p4.clone();
    p43.sub(p3);

    let d1321 = p13.dot(p21);
    let d2121 = p21.dot(p21);
    let d4321 = p43.dot(p21);
    let d1343 = p13.dot(p43);
    let d4343 = p43.dot(p43);

    let mua = (d1343 * d4321 - d1321 * d4343) / (d2121 * d4343 - d4321 * d4321);
    let mub = (d1343 + mua * d4321) / d4343;

    let muaP21 = p21.clone();
    muaP21.multiplyScalar(mua);
    let mubP43 = p43.clone();
    mubP43.multiplyScalar(mub);

    let pa = p1.clone();
    pa.add(muaP21);
    let pb = p3.clone();
    pb.add(mubP43);

    let interP = pa.clone();
    interP.add(pb);
    interP.multiplyScalar(0.5);
    return interP;
  }

  onDocumentMouseMove (event) {
    if (event.touches) {
      let e = event.touches[0];
      this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    } else {
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    }
    this.calcIntersected();

    let ghhandle = this.selectedHandle;
    if (ghhandle != null) {
      let handle3DPos = ghhandle.component.handlePos.clone();
      let handle2DPos = this.projectTo2D(handle3DPos);

      let drag2DDir = this.mouse.clone();
      drag2DDir.sub(handle2DPos);

      let start2DPos = this.projectTo2D(ghhandle.component.minPos);
      let end2DPos = this.projectTo2D(ghhandle.component.maxPos);
      let rail2DDir = end2DPos.clone();
      rail2DDir.sub(start2DPos);
      rail2DDir.normalize();

      let toStart2DDist = handle2DPos.distanceTo(start2DPos);
      let toEnd2DDist = handle2DPos.distanceTo(end2DPos);
      let move2DDist = drag2DDir.dot(rail2DDir);
      move2DDist = Math.min(Math.max(-toStart2DDist, move2DDist), toEnd2DDist);

      let new2DPos = handle2DPos.clone();
      let move2DDir = rail2DDir.clone();
      move2DDir.multiplyScalar(move2DDist);
      new2DPos.add(move2DDir);

      let unprojected3DPos1 = new THREE.Vector3(new2DPos.x, new2DPos.y, 1);
      let unprojected3DPos2 = new THREE.Vector3(new2DPos.x, new2DPos.y, -1);
      unprojected3DPos1.unproject(this.camera);
      unprojected3DPos2.unproject(this.camera);
      unprojected3DPos1.divideScalar(unit);
      unprojected3DPos2.divideScalar(unit);

      let ray3DDir1 = unprojected3DPos1.clone();
      ray3DDir1.sub(unprojected3DPos2);
      let ray3DDir2 = ghhandle.component.startPos.clone();
      ray3DDir2.sub(ghhandle.component.endPos.clone());

      let interP = this.calcIntersecttLines(unprojected3DPos1, unprojected3DPos2, ghhandle.component.startPos, ghhandle.component.maxPos);
      let dist1 = ghhandle.component.startPos.distanceTo(interP);
      let dist2 = ghhandle.component.startPos.distanceTo(ghhandle.component.maxPos.clone());
      let handleRatio = dist1 / dist2;

      ghhandle.updateRatio(handleRatio, true);
      this.update();

      this.parser.updateHandleComponents();
    }
  }

  onDocumentMouseDown (event) {
    if (event.touches) {
      let e = event.touches[0];
      this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    }

    this.calcIntersected();

    if (this.INTERSECTED instanceof GHHandle) {
      this.controls.enabled = false;
      this.selectedHandle = this.INTERSECTED;
    }
    this.tapPos = this.mouse.clone();
  }

  calcIntersected () {
    this.raycaster.setFromCamera(this.mouse, this.camera);

    var meshes = [];
    for (var i = 0; i < this.scene.children.length; i++) {
      var obj = this.scene.children[i];
      if (obj.type === 'Object3D') {
        for (var n = 0; n < obj.children.length; n++) {
          var child = obj.children[n];
          if (child.type === 'Mesh') {
            if (child.geometry.selectable === true) {
              meshes.push(child);
            }
          }
        }
      }
    }

    var intersects = this.raycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      if (this.INTERSECTED !== intersects[0].object) {
        if (this.INTERSECTED) {
          this.INTERSECTED.unhighlight();
          if (this.INTERSECTED.geometry.visible === true) {
            this.INTERSECTED.unhighlight();
          } else {
            this.INTERSECTED.hide();
          }
        }

        this.INTERSECTED = intersects[0].object;

        this.INTERSECTED.highlight();
      }
    } else {
      if (this.INTERSECTED) {
        this.INTERSECTED.unhighlight();
        if (this.INTERSECTED.geometry.visible === true) {
          this.INTERSECTED.unhighlight();
        } else {
          this.INTERSECTED.hide();
        }
      }
      this.INTERSECTED = null;
    }
  }

  onDocumentMouseUp (event) {
    if (this.INTERSECTED != null) {
      if (!(this.INTERSECTED instanceof GHHandle)) {
        this.INTERSECTED.geometry.setVisible(!this.INTERSECTED.geometry.visible);
        if (this.INTERSECTED.geometry.component != null) {
          this.INTERSECTED.geometry.component.visibles[this.INTERSECTED.geometry.index] = this.INTERSECTED.geometry.visible;
        }

        this.updateMesh();

        this.mouse = new THREE.Vector2(999999999, 999999999);
      }
    }

    if (this.selectedHandle != null) {
      this.controls.enabled = true;
      this.selectedHandle = null;
    }
  }

  updateMesh () {
    this.parser.updateGeometries();
    this.parser.initMeshes(this.scene);

    this.scene.remove(this.container);
    this.container = new THREE.Object3D();

    this.parser.meshes.forEach((mesh, i) => {
      mesh.scale.set(unit, unit, unit);
      mesh.rotation.set(-Math.PI * 0.5, 0, 0);

      if (mesh.geometry.visible === true) {
        mesh.setMaterial(this.materialType, i);
        mesh.castShadow = true;
      } else {
        mesh.setMaterial(MaterialTypes.TRANSPARENT, i);
        mesh.castShadow = false;
      }
      mesh.setTextures(Object.values(this.textures)[0]);
      mesh.material.needsUpdate = true;
      mesh.receiveShadow = false;
      this.container.add(mesh);
    });

    this.scene.add(this.container);
    this.centerize(this.parser.meshes);

    this.boundingBox.update(this.container);
  }

  visualizePaths () {
    this.clearPaths();

    try {
      this.parser.components.forEach(component => {
        if (!component.ghGeometryObject.hidden) {
          let values = Object.values(component.outputs);
          values.forEach((output) => {
            let dataTree = output.paramObject.value;
            if (dataTree !== undefined && dataTree !== null) {
              for (let i = 0; i < dataTree.branches.length; i++) {
                let arr = dataTree.branches[i];
                if ((arr instanceof Array) && arr.length > 0 && (arr[0] instanceof Array)) {
                  arr.forEach(paths => {
                    paths.forEach(path => {
                      let line = new GHLinePath(path);
                      this.pathContainer.add(line);
                    });
                  });
                }
              }
            }
          });
        }
      });
    } catch (e) {
      console.warn(e);
    }
  }

  clearPaths () {
    let children = this.pathContainer.children;
    for (let i = children.length - 1; i >= 0; i--) {
      this.pathContainer.remove(children[i]);
    }
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/controllers/GHXController.js